
import { useState } from 'react';
import { phases, allMonths } from '../../../mocks/roadmapData';

const statusConfig = {
  completed: { label: '완료', dot: 'bg-teal-500', bg: 'bg-teal-100', text: 'text-teal-700' },
  'in-progress': { label: '진행중', dot: 'bg-amber-500', bg: 'bg-amber-100', text: 'text-amber-700' },
  pending: { label: '예정', dot: 'bg-gray-300', bg: 'bg-gray-100', text: 'text-gray-500' },
};

const phaseColors: Record<string, { bg: string; text: string; light: string; border: string; gradient: string }> = {
  rose: { bg: 'bg-rose-500', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-200', gradient: 'from-rose-400 to-rose-600' },
  amber: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200', gradient: 'from-amber-400 to-amber-600' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', light: 'bg-teal-50', border: 'border-teal-200', gradient: 'from-teal-400 to-teal-600' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200', gradient: 'from-emerald-400 to-emerald-600' },
  cyan: { bg: 'bg-cyan-600', text: 'text-cyan-600', light: 'bg-cyan-50', border: 'border-cyan-200', gradient: 'from-cyan-400 to-cyan-600' },
};

export default function PhaseTimeline() {
  const [expandedPhase, setExpandedPhase] = useState<string | null>('phase-land');
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);

  const totalMonths = allMonths.length;

  const getBarGradient = (status: string, color: string) => {
    if (status === 'completed') return 'from-teal-400 to-teal-600';
    if (status === 'in-progress') return 'from-amber-300 to-amber-500';
    const map: Record<string, string> = {
      rose: 'from-rose-100 to-rose-300',
      amber: 'from-amber-100 to-amber-300',
      teal: 'from-teal-100 to-teal-300',
      emerald: 'from-emerald-100 to-emerald-300',
      cyan: 'from-cyan-100 to-cyan-300',
    };
    return map[color] || 'from-gray-100 to-gray-300';
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">단계별 상세 타임라인</h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            각 단계를 클릭하면 세부 작업 일정과 간트 차트를 확인할 수 있습니다.
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-6 mb-10">
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${cfg.dot}`}></div>
              <span className="text-sm text-gray-600">{cfg.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <i className="ri-flag-fill text-sm text-rose-500"></i>
            <span className="text-sm text-gray-600">마일스톤</span>
          </div>
        </div>

        <div className="space-y-4">
          {phases.map((phase) => {
            const colors = phaseColors[phase.color];
            const isOpen = expandedPhase === phase.id;
            const completedCount = phase.tasks.filter((t) => t.status === 'completed').length;

            return (
              <div
                key={phase.id}
                className={`bg-white rounded-2xl shadow-sm border transition-all ${isOpen ? colors.border : 'border-gray-100'}`}
              >
                {/* Phase Header */}
                <button
                  onClick={() => setExpandedPhase(isOpen ? null : phase.id)}
                  className="w-full flex items-center justify-between px-6 lg:px-8 py-5 cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${phase.icon} text-2xl ${colors.text}`}></i>
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xs font-bold ${colors.text} uppercase`}>{phase.phase}</span>
                        {phase.status === 'in-progress' && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full whitespace-nowrap">진행중</span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">{phase.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5 hidden sm:block">{phase.period} · 예산 {phase.budget}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="hidden md:flex items-center gap-3">
                      <div className="w-28 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${colors.gradient} rounded-full transition-all duration-500`}
                          style={{ width: `${phase.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-500 whitespace-nowrap">{completedCount}/{phase.tasks.length}</span>
                    </div>
                    <i className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {/* Expanded Gantt */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {/* Description */}
                    <div className="px-6 lg:px-8 py-4 bg-gray-50/50">
                      <p className="text-sm text-gray-600">{phase.description}</p>
                      {phase.milestones.length > 0 && (
                        <div className="flex flex-wrap gap-3 mt-3">
                          {phase.milestones.map((ms, idx) => (
                            <div
                              key={idx}
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                ms.done ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              <i className={`ri-flag-fill text-xs ${ms.done ? 'text-teal-500' : 'text-gray-400'}`}></i>
                              {ms.name}
                              <span className="text-gray-400 ml-1">{allMonths[ms.month]}</span>
                              {ms.done && <i className="ri-check-line text-teal-600"></i>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Gantt Chart */}
                    <div className="overflow-x-auto">
                      {/* Month Headers */}
                      <div className="flex min-w-[1200px]">
                        <div className="w-64 flex-shrink-0 px-6 py-3 bg-gray-50 border-r border-gray-100">
                          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">작업 항목</span>
                        </div>
                        <div className="flex-1 flex">
                          {allMonths.map((month, idx) => {
                            const isInPhase = idx >= phase.startMonth && idx <= phase.endMonth;
                            return (
                              <div
                                key={idx}
                                className={`flex-1 text-center py-3 text-xs border-r border-gray-50 ${
                                  isInPhase ? `${colors.light} ${colors.text} font-bold` : 'text-gray-300 bg-gray-50'
                                }`}
                                style={{ minWidth: '28px' }}
                              >
                                {idx % 3 === 0 ? month : ''}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Task Rows */}
                      {phase.tasks.map((task, taskIdx) => {
                        const taskKey = `${phase.id}-${taskIdx}`;
                        const isHovered = hoveredTask === taskKey;
                        const sCfg = statusConfig[task.status];

                        return (
                          <div
                            key={taskIdx}
                            className={`flex min-w-[1200px] border-t border-gray-50 transition-colors ${isHovered ? 'bg-gray-50/80' : ''}`}
                            onMouseEnter={() => setHoveredTask(taskKey)}
                            onMouseLeave={() => setHoveredTask(null)}
                          >
                            <div className="w-64 flex-shrink-0 px-6 py-3.5 border-r border-gray-100">
                              <div className="flex items-center gap-2.5">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sCfg.dot}`}></div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-800 truncate">{task.name}</div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`px-1.5 py-0.5 ${sCfg.bg} ${sCfg.text} text-xs rounded`}>{sCfg.label}</span>
                                    <span className="text-xs text-gray-400">{task.dept}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex-1 relative">
                              <div className="absolute inset-0 flex">
                                {allMonths.map((_, idx) => (
                                  <div key={idx} className="flex-1 border-r border-gray-50" style={{ minWidth: '28px' }}></div>
                                ))}
                              </div>
                              <div
                                className="absolute inset-y-0 flex items-center"
                                style={{
                                  left: `${(task.start / totalMonths) * 100}%`,
                                  width: `${(task.duration / totalMonths) * 100}%`,
                                }}
                              >
                                <div className="relative w-full cursor-pointer">
                                  <div className={`h-7 rounded-md bg-gradient-to-r ${getBarGradient(task.status, phase.color)} shadow-sm hover:shadow-md transition-all hover:scale-y-110`}>
                                    {task.status === 'in-progress' && (
                                      <div className="absolute inset-0 rounded-md overflow-hidden">
                                        <div className="h-full bg-gradient-to-r from-white/0 to-white/30 animate-pulse"></div>
                                      </div>
                                    )}
                                  </div>
                                  {isHovered && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-30 pointer-events-none">
                                      <div className="bg-gray-900 text-white rounded-xl px-4 py-3 shadow-xl min-w-[220px]">
                                        <div className="text-sm font-bold mb-1">{task.name}</div>
                                        <div className="flex items-center gap-3 text-xs text-gray-300">
                                          <span>
                                            <i className="ri-calendar-line mr-1"></i>
                                            {allMonths[task.start]} ~ {allMonths[Math.min(task.start + task.duration - 1, totalMonths - 1)]}
                                          </span>
                                          <span>
                                            <i className="ri-team-line mr-1"></i>
                                            {task.dept}
                                          </span>
                                        </div>
                                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-gray-900"></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
